import React from "react";
import { ToggleLeft, ToggleRight, BookOpen, ChevronDown, ChevronUp, Users, Activity, Quote, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import MarkdownText from "../../../shared/ui/MarkdownText";
import { getPaperStableId } from "../../../shared/utils/paper";

export default function PaperCard({ 
  paper, 
  isContextSelected, 
  onToggleContext, 
  showContextButton = true,
  contextDisabled = false,
  isBookmarked,
  onToggleBookmark,
  bookmarkDisabled,
  handleReferenceClick,
  setSelectedImage,
  index,
  searchMode,
  isLoading = false,
  isFirstResult = false,
  sourceSelectionNudgeActive = false,
  sourceSelectionNudgeKey = 0,
  impressionRef,
  onTitleClick,
  onFullTextClick
}) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = React.useState(true);
  const isScorePending = isLoading && (paper.relevance_score === undefined || paper.relevance_score === null);
  const isProMode = String(searchMode || "").trim().toLowerCase() === "pro";

  const getYear = () => {
    return paper.year || paper.pub_year || paper.publication_date?.substring(0, 4) || "N/A";
  };
  const stablePaperId = getPaperStableId(paper, index);

  return (
    <div
      ref={impressionRef}
      id={isFirstResult ? "first-paper-card" : undefined}
      data-paper-card-id={stablePaperId || undefined}
      data-first-paper={isFirstResult ? "true" : undefined}
      className={`group bg-white rounded-xl border transition-all duration-200 hover:shadow-md ${
      isContextSelected 
        ? "border-blue-500 ring-1 ring-blue-500 shadow-sm" 
        : "border-slate-200 hover:border-slate-300"
    }`}
    >
      <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-slate-50">
        <div className="flex items-center gap-3">
          {isScorePending ? (
            <span
              className="flex items-center justify-center bg-slate-100 text-slate-300 font-bold h-6 w-6 rounded-lg text-xs shadow-sm animate-pulse"
              title="Rank"
            >
              &nbsp;
            </span>
          ) : (
            <span className="flex items-center justify-center bg-blue-100 text-blue-700 font-bold h-6 w-6 rounded-lg text-xs shadow-sm" title="Rank">
              #{paper.rank || index + 1}
            </span>
          )}
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-semibold text-slate-900 text-sm" title={paper.journal}>
              {paper.journal || "Unknown Journal"}
            </span>
            <span className="text-xs text-slate-400 font-medium">
              {getYear()}
            </span>
            {paper.cited_by !== undefined && (
              <span className="text-xs text-slate-500 font-medium flex items-center gap-1">
                • {paper.cited_by} {t("dashboard.card.citations")}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {isScorePending ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-300 animate-pulse w-16">
              &nbsp;
            </span>
          ) : isProMode ? (
            <div className="flex items-center gap-2 text-right">
              <span className="text-[11px] font-medium text-slate-500">
                {t("dashboard.card.criteriaMatchLabel")}
              </span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                (paper.relevance_score || 0) > 0.8 
                  ? "bg-green-50 text-green-700 ring-1 ring-green-600/20" 
                  : (paper.relevance_score || 0) > 0.6 
                    ? "bg-yellow-50 text-yellow-700 ring-1 ring-yellow-600/20" 
                    : "bg-slate-50 text-slate-600 ring-1 ring-slate-500/20"
              }`}>
                {((paper.relevance_score || 0) * 100).toFixed(1)}% {t("dashboard.card.match")}
              </span>
            </div>
          ) : (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              (paper.relevance_score || 0) > 0.8 
                ? "bg-green-50 text-green-700 ring-1 ring-green-600/20" 
                : (paper.relevance_score || 0) > 0.6 
                  ? "bg-yellow-50 text-yellow-700 ring-1 ring-yellow-600/20" 
                  : "bg-slate-50 text-slate-600 ring-1 ring-slate-500/20"
            }`}>
              {((paper.relevance_score || 0) * 100).toFixed(1)}% {t("dashboard.card.match")}
            </span>
          )}

          <button
            onClick={onToggleBookmark}
            disabled={bookmarkDisabled}
            className={`p-1.5 rounded-lg transition-colors ${
              isBookmarked
                ? "bg-amber-100 text-amber-600 hover:bg-amber-200"
                : "text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            } ${bookmarkDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            title={
              isBookmarked
                ? t("dashboard.card.removeBookmark")
                : t("dashboard.card.bookmark")
            }
          >
            <Star className={`w-4 h-4 ${isBookmarked ? "fill-current" : ""}`} />
          </button>
          
          {showContextButton && (
            <button
              key={
                sourceSelectionNudgeActive
                  ? `source-selection-nudge-${sourceSelectionNudgeKey}`
                  : "source-selection-default"
              }
              onClick={(event) => onToggleContext(paper, event)}
              disabled={contextDisabled}
              data-context-button="true"
              className={`relative p-1 rounded-lg transition-colors ${
                isContextSelected
                  ? "bg-blue-100 text-blue-600 hover:bg-blue-200"
                  : "text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              } ${
                sourceSelectionNudgeActive ? "paper-source-nudge-button" : ""
              } ${contextDisabled ? "cursor-not-allowed opacity-50" : ""}`}
              title={
                isContextSelected
                  ? t("dashboard.card.removeFromSources")
                  : contextDisabled
                    ? t("paperAnalysis.sourceLimitReached")
                  : t("dashboard.card.addToSources")
              }
            >
              {isContextSelected ? (
                <ToggleRight className="w-6 h-6" />
              ) : (
                <ToggleLeft className="w-6 h-6" />
              )}
            </button>
          )}
        </div>
      </div>

      <div className="p-5 pt-4">
        <div className="mb-4">
          <h3 className="text-lg font-bold text-slate-900 leading-snug mb-2 group-hover:text-blue-600 transition-colors">
            <a
              href={paper.url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline decoration-blue-600/30 decoration-2 underline-offset-2"
              onClick={onTitleClick}
            >
              {paper.title}
            </a>
          </h3>
          <div className="text-sm text-slate-500 flex items-center gap-2 flex-wrap">
            <Users className="w-3.5 h-3.5 opacity-70 shrink-0" />
            <span>
              {paper.authors?.slice(0, 3).join(", ")}
              {paper.authors?.length > 3 && <span> et al.</span>}
            </span>
          </div>
        </div>

        {(paper.rationale || paper.research_question || paper.key_results) ? (
          <div className="bg-slate-50/80 rounded-lg p-4 mb-5 text-sm text-slate-700 leading-relaxed border border-slate-100 space-y-4">
            {paper.rationale && (
              <div>
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  {t("dashboard.card.rationale")}
                </p>
                <div className="flex items-start gap-2.5">
                  <Quote className="w-4 h-4 text-blue-500 shrink-0 mt-0.5 opacity-50" />
                  <span>{paper.rationale}</span>
                </div>
              </div>
            )}
            {paper.research_question && (
              <div>
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  {t("dashboard.card.researchQuestion")}
                </p>
                <div className="flex items-start gap-2.5">
                  <Quote className="w-4 h-4 text-blue-500 shrink-0 mt-0.5 opacity-50" />
                  <MarkdownText
                    content={paper.research_question}
                    className="min-w-0 flex-1"
                  />
                </div>
              </div>
            )}
            {paper.key_results && (
              <div>
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  {t("dashboard.card.keyResults")}
                </p>
                <div className="flex items-start gap-2.5">
                  <Quote className="w-4 h-4 text-blue-500 shrink-0 mt-0.5 opacity-50" />
                  <span>{paper.key_results}</span>
                </div>
              </div>
            )}
          </div>
        ) : isLoading ? (
          <div className="bg-slate-50/80 rounded-lg p-4 mb-5 border border-slate-100">
            <div className="space-y-2 animate-pulse">
              <div className="h-3 bg-slate-200 rounded w-11/12" />
              <div className="h-3 bg-slate-200 rounded w-10/12" />
              <div className="h-3 bg-slate-200 rounded w-8/12" />
            </div>
          </div>
        ) : null}

        <div className="flex flex-wrap gap-x-6 gap-y-3 mb-5">
          {paper.design ? (
            <div className="flex items-start gap-2 text-xs">
              <Activity className="w-3.5 h-3.5 text-indigo-500 shrink-0 mt-0.5" />
              <span className="text-slate-700 font-medium" title={t("dashboard.card.design")}>
                {paper.design}
              </span>
            </div>
          ) : isLoading ? (
            <div className="flex items-start gap-2 text-xs">
              <Activity className="w-3.5 h-3.5 text-slate-300 shrink-0 mt-0.5" />
              <span className="h-3 w-24 bg-slate-200 rounded animate-pulse" />
            </div>
          ) : null}
          
          {paper.population ? (
            <div className="flex items-start gap-2 text-xs">
              <Users className="w-3.5 h-3.5 text-teal-500 shrink-0 mt-0.5" />
              <span className="text-slate-700 font-medium" title={t("dashboard.card.populationLabel")}>
                {paper.population}
              </span>
            </div>
          ) : isLoading ? (
            <div className="flex items-start gap-2 text-xs">
              <Users className="w-3.5 h-3.5 text-slate-300 shrink-0 mt-0.5" />
              <span className="h-3 w-20 bg-slate-200 rounded animate-pulse" />
            </div>
          ) : null}

        </div>

        {paper.image_urls && paper.image_urls.length > 0 && (
          <div className="mb-5">
            <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar snap-x">
              {paper.image_urls.map((url, i) => (
                <div 
                  key={i}
                  className="h-16 w-16 sm:h-20 sm:w-20 flex-shrink-0 rounded-lg border border-slate-200 bg-white overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-500 hover:border-transparent transition-all snap-start"
                  onClick={() => setSelectedImage && setSelectedImage(url)} 
                >
                  <img src={url} alt={`Figure ${i+1}`} className="h-full w-full object-contain p-0.5" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <div className="flex gap-4">
            {paper.pmcid && (
              <a 
                href={`https://pmc.ncbi.nlm.nih.gov/articles/${paper.pmcid}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-full transition-colors border border-blue-100"
                onClick={onFullTextClick}
              >
                <BookOpen className="w-3.5 h-3.5" />
                {t("dashboard.card.fullText")}
              </a>
            )}
          </div>

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors bg-slate-50 hover:bg-slate-100 px-3 py-1.5 rounded-full"
          >
            {t("dashboard.card.studySnapshot")}
            {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="px-5 pb-5 animate-fadeIn">
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
            <div>
              <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">{t("dashboard.card.exposureIntervention")}</span>
              {paper.exposure_intervention ? (
                <span className="font-medium text-slate-800">{paper.exposure_intervention}</span>
              ) : isLoading ? (
                <span className="h-3 w-24 bg-slate-200 rounded animate-pulse block" />
              ) : (
                <span className="font-medium text-slate-800">-</span>
              )}
            </div>
            <div>
              <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">{t("dashboard.card.endpoint")}</span>
              {paper.endpoint ? (
                <span className="font-medium text-slate-800">{paper.endpoint}</span>
              ) : isLoading ? (
                <span className="h-3 w-24 bg-slate-200 rounded animate-pulse block" />
              ) : (
                <span className="font-medium text-slate-800">-</span>
              )}
            </div>
            <div>
              <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">{t("dashboard.card.statistics")}</span>
              {paper.statistics ? (
                <span className="font-medium text-slate-800">{paper.statistics}</span>
              ) : isLoading ? (
                <span className="h-3 w-24 bg-slate-200 rounded animate-pulse block" />
              ) : (
                <span className="font-medium text-slate-800">-</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
